import { InputFrame, emptyInput, clamp } from '@voidline/shared';
import { Action, BindingSet, Settings } from './persist.js';
import type { TouchControls } from './ui/touch.js';

/**
 * Input manager: keyboard + gamepad → InputFrame. Digital keys get smoothed steering;
 * gamepad sticks/triggers are analog. Bindings are rebindable at runtime.
 */
export class InputManager {
  private keysDown = new Set<string>();
  private smoothSteer = 0;
  private smoothPitch = 0;
  gamepadIndex: number | null = null;
  gamepadName = '';
  /** Set while the bindings screen captures the next key/button. */
  captureCallback: ((kind: 'key' | 'pad', code: string | number) => void) | null = null;
  private prevPadButtons: boolean[] = [];
  /** Fired on gamepad connect/disconnect for UI toasts. */
  onGamepadChange: ((name: string | null) => void) | null = null;
  /** UI menu navigation from gamepad (dpad/stick/face buttons). */
  onMenuNav: ((dir: 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back') => void) | null = null;
  private menuNavCooldown = 0;
  lookBack = false;
  private pauseQueued = false;
  /** Optional on-screen touch controls, merged into every sample. */
  touch: TouchControls | null = null;

  constructor(private settings: Settings) {
    window.addEventListener('keydown', (e) => {
      if (this.captureCallback && !e.repeat) {
        e.preventDefault();
        const cb = this.captureCallback;
        this.captureCallback = null;
        cb('key', e.code);
        return;
      }
      // don't steal typing from inputs
      if ((e.target as HTMLElement)?.tagName === 'INPUT') return;
      this.keysDown.add(e.code);
      if (this.isBound('pause', e.code)) this.pauseQueued = true;
      // block page scroll on game keys
      if (['Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.code)) e.preventDefault();
    });
    window.addEventListener('keyup', (e) => this.keysDown.delete(e.code));
    window.addEventListener('blur', () => this.keysDown.clear());
    window.addEventListener('gamepadconnected', (e) => {
      this.gamepadIndex = e.gamepad.index;
      this.gamepadName = e.gamepad.id;
      this.onGamepadChange?.(e.gamepad.id);
    });
    window.addEventListener('gamepaddisconnected', (e) => {
      if (this.gamepadIndex === e.gamepad.index) {
        this.gamepadIndex = null;
        this.gamepadName = '';
        this.onGamepadChange?.(null);
      }
    });
  }

  /** Consume a queued pause press (edge-triggered). */
  consumePause(): boolean {
    const p = this.pauseQueued;
    this.pauseQueued = false;
    return p;
  }

  private isBound(action: Action, code: string): boolean {
    return this.settings.bindings[action]?.keys.includes(code) ?? false;
  }

  private keyHeld(action: Action): boolean {
    const b = this.settings.bindings[action];
    if (!b) return false;
    for (const k of b.keys) if (this.keysDown.has(k)) return true;
    return false;
  }

  private pad(): Gamepad | null {
    if (this.gamepadIndex === null) return null;
    const pads = navigator.getGamepads?.();
    return pads?.[this.gamepadIndex] ?? null;
  }

  private padButton(gp: Gamepad, action: Action): number {
    const b = this.settings.bindings[action];
    let v = 0;
    for (const idx of b?.padButtons ?? []) {
      const btn = gp.buttons[idx];
      if (btn) v = Math.max(v, btn.value);
    }
    return v;
  }

  /**
   * Sample current device state into an InputFrame. dt for key smoothing.
   * Menu-nav events and pad capture also happen here (call every frame).
   */
  sample(dt: number, out: InputFrame): InputFrame {
    const gp = this.pad();

    // capture mode: watch for any pad button press
    if (gp && this.captureCallback) {
      for (let i = 0; i < gp.buttons.length; i++) {
        if (gp.buttons[i].pressed && !this.prevPadButtons[i]) {
          const cb = this.captureCallback;
          this.captureCallback = null;
          cb('pad', i);
          break;
        }
      }
    }

    // ── steering ──
    const sens = this.settings.steerSensitivity;
    let steerTarget = 0;
    if (this.keyHeld('steerLeft')) steerTarget -= 1;
    if (this.keyHeld('steerRight')) steerTarget += 1;
    let analogSteer = 0;
    if (gp) {
      const x = gp.axes[0] ?? 0;
      analogSteer = Math.abs(x) > 0.09 ? x : 0; // deadzone
      analogSteer += this.padButton(gp, 'steerRight') - this.padButton(gp, 'steerLeft');
    }
    if (analogSteer !== 0) {
      this.smoothSteer = clamp(analogSteer * sens, -1, 1);
    } else if (steerTarget !== 0) {
      // keys ramp up quickly but not instantly (kbd playability)
      this.smoothSteer = clamp(this.smoothSteer + steerTarget * dt * 6.5 * sens, -1, 1);
      if (Math.sign(this.smoothSteer) !== Math.sign(steerTarget)) this.smoothSteer = steerTarget * 0.25;
    } else {
      const decay = Math.exp(-dt * 11);
      this.smoothSteer *= decay;
      if (Math.abs(this.smoothSteer) < 0.02) this.smoothSteer = 0;
    }

    // ── pitch ──
    let pitchTarget = 0;
    if (this.keyHeld('pitchDown')) pitchTarget += 1;
    if (this.keyHeld('pitchUp')) pitchTarget -= 1;
    let analogPitch = 0;
    if (gp) {
      const y = gp.axes[1] ?? 0;
      analogPitch = Math.abs(y) > 0.12 ? y : 0;
    }
    if (analogPitch !== 0) this.smoothPitch = clamp(analogPitch, -1, 1);
    else if (pitchTarget !== 0) this.smoothPitch = clamp(this.smoothPitch + pitchTarget * dt * 5, -1, 1);
    else this.smoothPitch *= Math.exp(-dt * 8);
    if (this.settings.invertPitch) this.smoothPitch = -this.smoothPitch;

    // ── throttle / drift / brake / boost ──
    let throttle = this.keyHeld('throttle') ? 1 : 0;
    let drift = this.keyHeld('drift') ? 1 : 0;
    const brake = this.keyHeld('brake') ? 1 : 0;
    let brakeL = 0;
    let brakeR = 0;
    let boost = this.keyHeld('boost');
    let respawn = this.keyHeld('respawn');
    this.lookBack = this.keyHeld('lookBack');

    if (gp) {
      throttle = Math.max(throttle, this.padButton(gp, 'throttle'));
      drift = Math.max(drift, this.padButton(gp, 'drift'));
      const gpBrake = this.padButton(gp, 'brake');
      if (gpBrake > 0.05) {
        brakeL = Math.max(brakeL, gpBrake);
        brakeR = Math.max(brakeR, gpBrake);
      }
      boost = boost || this.padButton(gp, 'boost') > 0.5;
      respawn = respawn || this.padButton(gp, 'respawn') > 0.5;
      this.lookBack = this.lookBack || this.padButton(gp, 'lookBack') > 0.5;
      if (this.padButton(gp, 'pause') > 0.5 && !this.prevPadPause) this.pauseQueued = true;
      this.prevPadPause = this.padButton(gp, 'pause') > 0.5;
    }
    // brake key = both airbrakes (straight-line slow-down)
    if (brake > 0) {
      brakeL = Math.max(brakeL, brake);
      brakeR = Math.max(brakeR, brake);
    }

    // ── menu navigation from pad ──
    if (gp && this.onMenuNav) {
      this.menuNavCooldown -= dt;
      const y = gp.axes[1] ?? 0;
      const x = gp.axes[0] ?? 0;
      const navUp = (gp.buttons[12]?.pressed ?? false) || y < -0.6;
      const navDown = (gp.buttons[13]?.pressed ?? false) || y > 0.6;
      const navLeft = (gp.buttons[14]?.pressed ?? false) || x < -0.6;
      const navRight = (gp.buttons[15]?.pressed ?? false) || x > 0.6;
      const confirm = gp.buttons[0]?.pressed ?? false;
      const back = gp.buttons[1]?.pressed ?? false;
      if (this.menuNavCooldown <= 0) {
        if (navUp) this.emitNav('up');
        else if (navDown) this.emitNav('down');
        else if (navLeft) this.emitNav('left');
        else if (navRight) this.emitNav('right');
        else if (confirm && !this.prevConfirm) this.emitNav('confirm');
        else if (back && !this.prevBack) this.emitNav('back');
      }
      this.prevConfirm = confirm;
      this.prevBack = back;
    }
    if (gp) this.prevPadButtons = gp.buttons.map((b) => b.pressed);

    // ── touch controls (phones/tablets) ──
    const touch = this.touch;
    if (touch?.used) {
      const ts = touch.state;
      if (ts.driving) {
        // finger held in the drive zone: full throttle, drag steers/pitches
        throttle = Math.max(throttle, 1);
        this.smoothSteer = ts.steer * sens;
        this.smoothPitch = this.settings.invertPitch ? -ts.pitch : ts.pitch;
      }
      if (ts.boost) boost = true;
      if (ts.drift) drift = 1;
      if (touch.consumeRespawn(dt)) respawn = true;
    }

    // ── drift: one button, steering picks the side ──
    // The airbrake engages on the side you're steering toward (the expert inside-
    // brake line); held straight it's a symmetric partial brake.
    if (drift > 0.05) {
      if (this.smoothSteer > 0.15) brakeR = Math.max(brakeR, drift);
      else if (this.smoothSteer < -0.15) brakeL = Math.max(brakeL, drift);
      else {
        brakeL = Math.max(brakeL, drift * 0.6);
        brakeR = Math.max(brakeR, drift * 0.6);
      }
    }

    out.steer = this.smoothSteer;
    out.pitch = this.smoothPitch;
    out.throttle = throttle;
    out.brakeL = brakeL;
    out.brakeR = brakeR;
    out.boost = boost;
    out.respawn = respawn;
    return out;
  }

  private prevPadPause = false;
  private prevConfirm = false;
  private prevBack = false;

  private emitNav(dir: 'up' | 'down' | 'left' | 'right' | 'confirm' | 'back'): void {
    this.onMenuNav?.(dir);
    this.menuNavCooldown = dir === 'confirm' || dir === 'back' ? 0.05 : 0.22;
  }

  /** Rebind: set the next pressed key/button as binding for action. */
  captureNext(action: Action, slot: 'key' | 'pad', done: () => void): void {
    this.captureCallback = (kind, code) => {
      const b: BindingSet = this.settings.bindings[action];
      if (kind === 'key' && slot === 'key' && typeof code === 'string') {
        b.keys = [code, ...b.keys.filter((k) => k !== code)].slice(0, 2);
      } else if (kind === 'pad' && slot === 'pad' && typeof code === 'number') {
        b.padButtons = [code];
      }
      done();
    };
  }

  cancelCapture(): void {
    this.captureCallback = null;
  }
}

export function makeInputFrame(): InputFrame {
  return emptyInput();
}

/** Human-readable label for a KeyboardEvent.code. */
export function keyLabel(code: string): string {
  if (code.startsWith('Key')) return code.slice(3);
  if (code.startsWith('Digit')) return code.slice(5);
  if (code.startsWith('Arrow')) return code.slice(5).toUpperCase();
  const map: Record<string, string> = {
    ShiftLeft: 'L-SHIFT',
    ShiftRight: 'R-SHIFT',
    ControlLeft: 'L-CTRL',
    Space: 'SPACE',
    Escape: 'ESC',
    Enter: 'ENTER',
  };
  return map[code] ?? code.toUpperCase();
}

export const PAD_BUTTON_LABELS: Record<number, string> = {
  0: 'A / ✕',
  1: 'B / ○',
  2: 'X / □',
  3: 'Y / △',
  4: 'LB',
  5: 'RB',
  6: 'LT',
  7: 'RT',
  8: 'SELECT',
  9: 'START',
  10: 'L3',
  11: 'R3',
  12: 'D-UP',
  13: 'D-DOWN',
  14: 'D-LEFT',
  15: 'D-RIGHT',
};
